import { computed, ComputedRef, Ref, ref, watch } from "vue";
import dayjs from "dayjs";
import useToast from "@/ui/composables/useToast";
import { PlanEditTarget, useBasePriceStore } from "@/modules/base_price/store";
import {
	calendarWeeks,
	CalendarWeek,
	DayMap,
	impressionShares,
	monthDates,
} from "@/modules/base_price/utils/dayDistribution";

/**
 * What every month calendar in the editor shares, whichever tab it belongs
 * to: the month's dates and ISO weeks, and its impressions day by day for
 * whatever the editor is pointed at.
 *
 * The impressions are loaded on a new target, book or month — never on every
 * resolve, which would reload them under an edit in progress — and
 * `onLoaded` runs once they are in, since each calendar seeds its draft from
 * them. A newer request supersedes an older one still in flight.
 */
export const useMonthCalendar = (
	target: ComputedRef<PlanEditTarget>,
	month: Ref<number>,
	onLoaded: () => void
) => {
	const store = useBasePriceStore();

	const year = computed(() => store.plan?.year ?? dayjs().year());
	const dates = computed(() => monthDates(year.value, month.value));
	const weeks = computed<CalendarWeek[]>(() =>
		calendarWeeks(year.value, month.value)
	);
	/** `YYYY-MM` — how a day-level row lists the months it decides. */
	const monthKey = computed(
		() => `${year.value}-${String(month.value).padStart(2, "0")}`
	);
	const monthName = computed(() =>
		dayjs(new Date(year.value, month.value - 1, 1)).format("MMMM")
	);
	const monthIndex = computed(() => month.value - 1);

	const dailyImpressions = ref<DayMap<number>>({});
	const loadingImpressions = ref(false);

	/** Each day's share (0-100) of the month's impressions. */
	const shares = computed(() =>
		impressionShares(dates.value, dailyImpressions.value)
	);

	/**
	 * A day's gross impressions in this book. The daily endpoint sums the raw
	 * source, before any reservation book took its share, so its shape is
	 * scaled onto the book's own monthly figure — the one every other number
	 * on screen is measured against.
	 */
	const dayGross = (date: string): number =>
		((target.value.monthly_gross_impressions[monthIndex.value] ?? 0) *
			(shares.value[date] ?? 0)) /
		100;

	let loadToken = 0;

	const load = async () => {
		const token = ++loadToken;
		loadingImpressions.value = true;

		const response = await store.fetchDailyImpressions(month.value);
		if (token !== loadToken) return;

		loadingImpressions.value = false;

		if (response?.isErr()) {
			useToast().error(response.error.message);
		}

		dailyImpressions.value = response?.isOk() ? response.value.days : {};

		onLoaded();
	};

	watch(
		() => [
			target.value.kind,
			target.value.id,
			store.selectedBookUuid,
			month.value,
		],
		load,
		{ immediate: true }
	);

	/** `1–6 Dec` — the part of a week that falls in this month. */
	const weekRange = (week: CalendarWeek): string => {
		const first = dayjs(week.inMonth[0]);
		const last = dayjs(week.inMonth[week.inMonth.length - 1]);

		return `${first.format("D")}–${last.format("D MMM")}`;
	};

	const step = (delta: number) => {
		month.value = Math.min(12, Math.max(1, month.value + delta));
	};

	return {
		year,
		dates,
		weeks,
		monthKey,
		monthName,
		monthIndex,
		dailyImpressions,
		loadingImpressions,
		shares,
		dayGross,
		weekRange,
		step,
	};
};
